import React, { useState, useEffect } from 'react';
import { fmtDate } from '@shared/formatDate';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  AccountBalance as BusinessIcon,
  FamilyRestroom as FamilyIcon,
  Visibility as ViewIcon,
  People as PeopleIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import jointAccountService, { JointAccountSummary, CreateJointAccountRequest } from '../services/jointAccountService';

const JointAccounts: React.FC = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<JointAccountSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // New account form - default to FAMILY since any user can create those
  const [newAccount, setNewAccount] = useState<CreateJointAccountRequest>({
    accountType: 'FAMILY',
    accountName: '',
    description: '',
    isFamilyAccount: true,
    requireParentalApproval: true,
    defaultDailyLimit: undefined,
    defaultPerTransactionLimit: undefined,
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await jointAccountService.getMyAccounts();
      setAccounts(data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load joint accounts');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    if (!newAccount.accountName.trim()) {
      setCreateError('Account name is required');
      return;
    }

    try {
      setCreating(true);
      setCreateError(null);
      const result = await jointAccountService.createAccount(newAccount);
      if (result.success) {
        setCreateDialogOpen(false);
        setNewAccount({
          accountType: 'FAMILY',
          accountName: '',
          description: '',
          isFamilyAccount: true,
          requireParentalApproval: true,
        });
        loadAccounts();
      } else {
        setCreateError(result.message || 'Failed to create account');
      }
    } catch (err: any) {
      setCreateError(err.response?.data?.message || 'Failed to create account');
    } finally {
      setCreating(false);
    }
  };

  const handleAccountTypeChange = (type: 'BUSINESS' | 'FAMILY') => {
    setNewAccount({
      ...newAccount,
      accountType: type,
      isFamilyAccount: type === 'FAMILY',
      requireParentalApproval: type === 'FAMILY',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateStr: string) => fmtDate(dateStr);

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'PRIMARY':
        return 'primary';
      case 'ADMIN':
        return 'secondary';
      case 'MINOR':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Joint Accounts</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Account
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {(!accounts || accounts.length === 0) ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Joint Accounts Yet
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Create a family account to give minors limited spending access, or a business account to share funds with team members.
          </Typography>
          <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
            <Typography variant="body2">
              <strong>Family Accounts:</strong> Any user can create these to add minors (children) with limited spending access.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Business Accounts:</strong> Only credentialed business accounts can create these to add adult team members.
            </Typography>
          </Alert>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Your First Account
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {accounts.map((account) => (
            <Grid item xs={12} md={6} lg={4} key={account.jointAccountId}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {account.accountType === 'FAMILY' ? (
                      <FamilyIcon color="primary" />
                    ) : (
                      <BusinessIcon color="primary" />
                    )}
                    <Typography variant="h6" noWrap>
                      {account.accountName}
                    </Typography>
                  </Box>

                  <Box display="flex" gap={1} mb={2}>
                    <Chip
                      label={account.accountType}
                      size="small"
                      color={account.accountType === 'FAMILY' ? 'warning' : 'info'}
                    />
                    <Chip
                      label={account.userRole}
                      size="small"
                      color={getRoleColor(account.userRole) as any}
                      variant="outlined"
                    />
                  </Box>

                  <Typography variant="h5" color="primary" gutterBottom>
                    {formatCurrency(account.availableBalance)}
                  </Typography>

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      <PeopleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      {account.memberCount} member{account.memberCount !== 1 ? 's' : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last activity: {formatDate(account.lastActivity)}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions>
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/joint-accounts/${account.jointAccountId}`)}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Members">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/joint-accounts/${account.jointAccountId}/members`)}
                    >
                      <PeopleIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="History">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/joint-accounts/${account.jointAccountId}/history`)}
                    >
                      <HistoryIcon />
                    </IconButton>
                  </Tooltip>
                  {(account.userRole === 'PRIMARY' || account.userRole === 'ADMIN') && (
                    <Tooltip title="Settings">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/joint-accounts/${account.jointAccountId}/settings`)}
                      >
                        <SettingsIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Account Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Joint Account</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Account Type</InputLabel>
            <Select
              value={newAccount.accountType}
              label="Account Type"
              onChange={(e) => handleAccountTypeChange(e.target.value as 'BUSINESS' | 'FAMILY')}
            >
              <MenuItem value="FAMILY">
                <Box display="flex" alignItems="center" gap={1}>
                  <FamilyIcon />
                  Family Account (for minors)
                </Box>
              </MenuItem>
              <MenuItem value="BUSINESS">
                <Box display="flex" alignItems="center" gap={1}>
                  <BusinessIcon />
                  Business Account (credentialed only)
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {newAccount.accountType === 'FAMILY' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Family accounts allow you to create limited-access aliases for minors (children). 
              Minors can spend within set limits but cannot deposit or withdraw funds.
            </Alert>
          )}

          {newAccount.accountType === 'BUSINESS' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Business accounts require a credentialed business account. You can add existing users
              who will be assigned a new username for this business account.
            </Alert>
          )}

          <TextField
            fullWidth
            label="Account Name"
            value={newAccount.accountName}
            onChange={(e) => setNewAccount({ ...newAccount, accountName: e.target.value })}
            placeholder={newAccount.accountType === 'FAMILY' ? 'Smith Family Fund' : 'Band Revenue Account'}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Description (optional)"
            value={newAccount.description}
            onChange={(e) => setNewAccount({ ...newAccount, description: e.target.value })}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" gutterBottom>
            Default Spending Limits (optional)
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Daily Limit"
                type="number"
                value={newAccount.defaultDailyLimit || ''}
                onChange={(e) => setNewAccount({
                  ...newAccount,
                  defaultDailyLimit: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Per Transaction Limit"
                type="number"
                value={newAccount.defaultPerTransactionLimit || ''}
                onChange={(e) => setNewAccount({
                  ...newAccount,
                  defaultPerTransactionLimit: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>
          </Grid>

          {newAccount.accountType === 'FAMILY' && (
            <Box mt={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newAccount.requireParentalApproval}
                    onChange={(e) => setNewAccount({
                      ...newAccount,
                      requireParentalApproval: e.target.checked
                    })}
                  />
                }
                label="Require parental approval for minor spending"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateAccount}
            disabled={creating}
          >
            {creating ? <CircularProgress size={24} /> : 'Create Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default JointAccounts;
